<template>
    <div class="admin-activity-edit" v-loading="loading">
        <div class="page-header">
            <el-button icon="ArrowLeft" @click="$router.push('/admin/activities')" link>返回列表</el-button>
            <h2>编辑活动: {{ activity.title }}</h2>
            <div class="header-actions">
                <el-tag :type="activity.status === 'published' ? 'success' : 'info'" class="status-tag">
                    {{ activity.status === 'published' ? '已发布' : '草稿' }}
                </el-tag>
                <el-button type="primary" @click="saveBasicInfo" :loading="saving">保存修改</el-button>
            </div>
        </div>

        <el-tabs v-model="activeTab" class="activity-tabs" type="border-card">
            <!-- 1. Basic Info -->
            <el-tab-pane label="基本信息" name="basic">
                <el-form :model="activity" label-width="100px" class="basic-form">
                    <el-row :gutter="20">
                        <el-col :span="16">
                            <el-form-item label="标题" required>
                                <el-input v-model="activity.title" />
                            </el-form-item>
                            <el-form-item label="标签">
                                <el-select v-model="activity.tags" multiple filterable allow-create default-first-option
                                    placeholder="选择或输入标签" style="width: 100%">
                                </el-select>
                            </el-form-item>
                            <el-row>
                                <el-col :span="12">
                                    <el-form-item label="价格">
                                        <el-input-number v-model="activity.price" :min="0" :precision="2" />
                                    </el-form-item>
                                </el-col>
                                <el-col :span="12">
                                    <el-form-item label="状态">
                                        <el-switch v-model="activity.status" active-value="published"
                                            inactive-value="draft" active-text="发布" inactive-text="草稿" />
                                    </el-form-item>
                                </el-col>
                            </el-row>
                        </el-col>
                        <el-col :span="8">
                            <el-form-item label="封面图">
                                <div class="cover-uploader" @click="triggerUpload">
                                    <el-image v-if="activity.coverImage" :src="activity.coverImage"
                                        class="cover-preview" fit="cover" />
                                    <div v-else class="upload-placeholder">
                                        <el-icon>
                                            <Plus />
                                        </el-icon>
                                        <span>上传封面</span>
                                    </div>
                                    <input type="file" ref="fileInput" hidden @change="handleUpload" accept="image/*" />
                                </div>
                            </el-form-item>
                        </el-col>
                    </el-row>
                    <el-form-item label="活动介绍">
                        <md-editor v-model="activity.description" style="height: 400px" />
                    </el-form-item>
                </el-form>
            </el-tab-pane>

            <!-- 2. Content (Chapter Tree) -->
            <el-tab-pane label="内容管理" name="content">
                <div class="tree-actions">
                    <el-button type="primary" size="small" @click="addNode('chapter')">添加章</el-button>
                    <span class="tip">提示：支持 课程 > 章 > 节 > 试卷 三级结构。直接上传试卷设为活动专用。</span>
                </div>

                <el-tree :data="nodes" node-key="id" default-expand-all :expand-on-click-node="false"
                    class="chapter-tree">
                    <template #default="{ node, data }">
                        <div class="custom-tree-node">
                            <span class="node-label">
                                <el-icon class="node-icon">
                                    <FolderOpened v-if="data.nodeType !== 'paper'" />
                                    <Document v-else />
                                </el-icon>
                                {{ data.title }}
                            </span>
                            <span class="node-ops">
                                <el-button v-if="data.nodeType === 'chapter'" type="primary" link size="small"
                                    @click="addNode('section', data)">加节</el-button>
                                <el-button v-if="data.nodeType === 'section'" type="primary" link size="small"
                                    @click="addPaper(data)">加试卷</el-button>
                                <el-button type="primary" link size="small" @click="editNode(data)">编辑</el-button>
                                <el-button type="danger" link size="small" @click="deleteNode(data)">删除</el-button>
                            </span>
                        </div>
                    </template>
                </el-tree>
            </el-tab-pane>

            <!-- 3. Videos -->
            <el-tab-pane label="视频专区" name="videos">
                <div class="video-header">
                    <el-button type="primary" icon="Plus" @click="showVideoDialog = true">添加视频</el-button>
                </div>
                <el-table :data="videos" border style="margin-top: 15px">
                    <el-table-column label="封面" width="120">
                        <template #default="{ row }">
                            <el-image :src="row.coverUrl" style="width: 100px; height: 56px" fit="cover" />
                        </template>
                    </el-table-column>
                    <el-table-column prop="title" label="视频标题" />
                    <el-table-column label="时长" width="100">
                        <template #default="{ row }">{{ formatDuration(row.duration) }}</template>
                    </el-table-column>
                    <el-table-column label="操作" width="120">
                        <template #default="{ row }">
                            <el-button link type="primary" @click="editVideo(row)">编辑</el-button>
                            <el-button link type="danger" @click="deleteVideo(row)">删除</el-button>
                        </template>
                    </el-table-column>
                </el-table>
            </el-tab-pane>
        </el-tabs>

        <!-- Dialogs -->
        <!-- Node Add/Edit -->
        <el-dialog v-model="showNodeDialog" :title="nodeForm.id ? '编辑节点' : '添加节点'" width="450px">
            <el-form :model="nodeForm" label-width="80px">
                <el-form-item label="类型">
                    <el-tag>{{ nodeTypeName[nodeForm.nodeType] }}</el-tag>
                </el-form-item>
                <el-form-item label="标题" required>
                    <el-input v-model="nodeForm.title" />
                </el-form-item>
                <el-form-item label="排序">
                    <el-input-number v-model="nodeForm.sortOrder" />
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="showNodeDialog = false">取消</el-button>
                <el-button type="primary" @click="saveNode">确定</el-button>
            </template>
        </el-dialog>

        <!-- Paper Add Dialog -->
        <el-dialog v-model="showPaperDialog" title="添加试卷到节" width="600px">
            <el-tabs v-model="paperSourceTab">
                <el-tab-pane label="从试卷中心选择" name="select">
                    <div class="search-box">
                        <el-input v-model="paperSearch" placeholder="搜索试卷..." @keyup.enter="fetchPublicPapers">
                            <template #append><el-button icon="Search" @click="fetchPublicPapers" /></template>
                        </el-input>
                    </div>
                    <el-table :data="publicPapers" height="300px" v-loading="papersLoading"
                        @current-change="handlePaperSelect" highlight-current-row>
                        <el-table-column prop="name" label="试卷名称" />
                        <el-table-column prop="category" label="分类" width="100" />
                    </el-table>
                    <div class="dialog-footer-inline">
                        <el-button type="primary" :disabled="!selectedPaper"
                            @click="submitAddPaper('existing')">确定选择</el-button>
                    </div>
                </el-tab-pane>
                <el-tab-pane label="直接上传文件" name="upload">
                    <div class="upload-area">
                        <el-upload class="paper-file-uploader" drag action="/api/papers/upload-file"
                            :headers="authHeaders" :on-success="handlePaperUploadSuccess">
                            <el-icon class="el-icon--upload">
                                <UploadFilled />
                            </el-icon>
                            <div class="el-upload__text">拖拽或点击上传试卷文件 <em>(docx/pdf/zip)</em></div>
                        </el-upload>
                        <div v-if="tempPaperFile.fileUrl" class="uploaded-paper-info">
                            已上传: <b>{{ tempPaperFile.fileName }}</b>
                            <el-button type="primary" @click="submitAddPaper('new')"
                                style="display: block; margin-top: 10px">保存到活动</el-button>
                        </div>
                    </div>
                </el-tab-pane>
            </el-tabs>
        </el-dialog>

        <!-- Video Dialog -->
        <el-dialog v-model="showVideoDialog" title="视频管理" width="500px">
            <el-form :model="videoForm" label-width="80px">
                <el-form-item label="标题" required>
                    <el-input v-model="videoForm.title" />
                </el-form-item>
                <el-form-item label="视频URL" required>
                    <el-input v-model="videoForm.videoUrl" placeholder="OSS地址或外链" />
                </el-form-item>
                <el-form-item label="封面URL">
                    <el-input v-model="videoForm.coverUrl" />
                </el-form-item>
                <el-form-item label="时长(秒)">
                    <el-input-number v-model="videoForm.duration" />
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="showVideoDialog = false">取消</el-button>
                <el-button type="primary" @click="saveVideo">确定</el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus, ArrowLeft, FolderOpened, Document, Search, UploadFilled } from '@element-plus/icons-vue'
import api from '@/utils/api'
import MdEditor from 'md-editor-v3'
import 'md-editor-v3/lib/style.css'

const router = useRouter()
const route = useRoute()
const activityId = route.params.id

const loading = ref(false)
const saving = ref(false)
const activeTab = ref('basic')
const activity = ref({
    title: '',
    description: '',
    coverImage: '',
    status: 'draft',
    price: 0,
    tags: []
})

const nodes = ref([])
const videos = ref([])

// --- Basic Info ---
const fileInput = ref(null)
const triggerUpload = () => fileInput.value.click()
const handleUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    const formData = new FormData()
    formData.append('file', file)
    formData.append('type', 'activity')
    try {
        const res = await api.post('/api/upload/image', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        })
        activity.value.coverImage = res.data.url
    } catch (e) {
        ElMessage.error(e.response?.data?.error || '上传失败')
    }
}

const fetchActivity = async () => {
    loading.value = true
    try {
        const res = await api.get(`/api/activities/${activityId}`)
        activity.value = res.data.activity
        if (!activity.value.tags) activity.value.tags = []

        // Fetch nodes
        const nodeRes = await api.get(`/api/activities/${activityId}/nodes`)
        nodes.value = nodeRes.data.nodes || []

        // Fetch videos
        const videoRes = await api.get(`/api/activities/${activityId}/videos`)
        videos.value = videoRes.data.videos || []
    } catch (e) { ElMessage.error('加载详情失败') }
    finally { loading.value = false }
}

const saveBasicInfo = async () => {
    saving.value = true
    try {
        await api.put(`/api/admin/activities/${activityId}`, activity.value)
        ElMessage.success('保存成功')
    } catch (e) { ElMessage.error('保存失败') }
    finally { saving.value = false }
}

// --- Nodes (Chapter Tree) ---
const showNodeDialog = ref(false)
const nodeForm = ref({ id: null, title: '', nodeType: 'chapter', activityId: Number(activityId), parentId: null, sortOrder: 0 })
const nodeTypeName = { chapter: '章', section: '节', paper: '试卷' }

const addNode = (type, parent = null) => {
    nodeForm.value = { id: null, title: '', nodeType: type, activityId: Number(activityId), parentId: parent ? parent.id : null, sortOrder: 0 }
    showNodeDialog.value = true
}

const editNode = (data) => {
    nodeForm.value = { ...data }
    showNodeDialog.value = true
}

const saveNode = async () => {
    try {
        if (nodeForm.value.id) {
            await api.put(`/api/admin/activities/nodes/${nodeForm.value.id}`, nodeForm.value)
        } else {
            await api.post(`/api/admin/activities/${activityId}/nodes`, nodeForm.value)
        }
        ElMessage.success('保存成功')
        showNodeDialog.value = false
        const res = await api.get(`/api/activities/${activityId}/nodes`)
        nodes.value = res.data.nodes || []
    } catch (e) { ElMessage.error('操作失败') }
}

const deleteNode = (data) => {
    ElMessageBox.confirm('确定删除此节点及所有子项吗？', '确认').then(async () => {
        await api.delete(`/api/admin/activities/nodes/${data.id}`)
        ElMessage.success('已删除')
        const res = await api.get(`/api/activities/${activityId}/nodes`)
        nodes.value = res.data.nodes || []
    })
}

// --- Papers ---
const showPaperDialog = ref(false)
const paperSourceTab = ref('select')
const paperSearch = ref('')
const publicPapers = ref([])
const papersLoading = ref(false)
const selectedPaper = ref(null)
const currentParentNodeId = ref(null)
const tempPaperFile = ref({ fileUrl: '', fileName: '', fileType: '' })

const authHeaders = { Authorization: `Bearer ${localStorage.getItem('token')}` }

const addPaper = (sectionNode) => {
    currentParentNodeId.value = sectionNode.id
    showPaperDialog.value = true
    fetchPublicPapers()
}

const fetchPublicPapers = async () => {
    papersLoading.value = true
    try {
        const res = await api.get('/api/papers', { params: { keyword: paperSearch.value, pageSize: 20 } })
        publicPapers.value = res.data.papers || []
    } finally { papersLoading.value = false }
}

const handlePaperSelect = (row) => {
    selectedPaper.value = row
}

const handlePaperUploadSuccess = (res) => {
    tempPaperFile.value = {
        fileUrl: res.url,
        fileName: res.fileName,
        fileType: res.fileType
    }
}

const submitAddPaper = async (mode) => {
    try {
        const payload = {
            activityId: Number(activityId),
            parentId: currentParentNodeId.value,
            nodeType: 'paper',
            sortOrder: 0
        }

        if (mode === 'existing') {
            payload.paperId = selectedPaper.value.id
            payload.title = selectedPaper.value.name
        } else {
            payload.fileUrl = tempPaperFile.value.fileUrl
            payload.fileName = tempPaperFile.value.fileName
            payload.fileType = tempPaperFile.value.fileType
            payload.title = tempPaperFile.value.fileName

            // Also register this paper as activity-only in backend
            const paperRes = await api.post('/api/papers/create-with-file', {
                name: tempPaperFile.value.fileName,
                fileUrl: tempPaperFile.value.fileUrl,
                fileType: tempPaperFile.value.fileType,
                fileName: tempPaperFile.value.fileName,
                isActivityOnly: true, // We should ensure our backend handles this
                status: 'published'
            })
            payload.paperId = paperRes.data.id
        }

        await api.post(`/api/admin/activities/${activityId}/nodes`, payload)
        ElMessage.success('添加试卷成功')
        showPaperDialog.value = false
        const res = await api.get(`/api/activities/${activityId}/nodes`)
        nodes.value = res.data.nodes || []
    } catch (e) { ElMessage.error('添加失败') }
}

// --- Videos ---
const showVideoDialog = ref(false)
const videoForm = ref({ id: null, title: '', videoUrl: '', coverUrl: '', duration: 0, activityId: Number(activityId) })

const editVideo = (row) => {
    videoForm.value = { ...row }
    showVideoDialog.value = true
}

const saveVideo = async () => {
    try {
        if (videoForm.value.id) {
            await api.put(`/api/admin/activities/videos/${videoForm.value.id}`, videoForm.value)
        } else {
            await api.post(`/api/admin/activities/${activityId}/videos`, videoForm.value)
        }
        ElMessage.success('视频保存成功')
        showVideoDialog.value = false
        const res = await api.get(`/api/activities/${activityId}/videos`)
        videos.value = res.data.videos || []
    } catch (e) { ElMessage.error('操作失败') }
}

const deleteVideo = (row) => {
    ElMessageBox.confirm('确定删除此视频吗？').then(async () => {
        await api.delete(`/api/admin/activities/videos/${row.id}`)
        ElMessage.success('已删除')
        const res = await api.get(`/api/activities/${activityId}/videos`)
        videos.value = res.data.videos || []
    })
}

const formatDuration = (s) => {
    const m = Math.floor(s / 60)
    const rs = s % 60
    return `${m}:${rs < 10 ? '0' : ''}${rs}`
}

onMounted(fetchActivity)
</script>

<style scoped>
.admin-activity-edit {
    max-width: 1200px;
    margin: 0 auto;
}

.page-header {
    display: flex;
    align-items: center;
    gap: 20px;
    margin-bottom: 24px;
}

.page-header h2 {
    flex: 1;
    margin: 0;
    font-size: 20px;
}

.header-actions {
    display: flex;
    align-items: center;
    gap: 15px;
}

.activity-tabs {
    margin-top: 20px;
}

.basic-form {
    padding: 10px;
}

.cover-uploader {
    width: 100%;
    aspect-ratio: 16/9;
    border: 1px dashed #d9d9d9;
    border-radius: 6px;
    cursor: pointer;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fafafa;
}

.cover-preview {
    width: 100%;
    height: 100%;
}

.upload-placeholder {
    display: flex;
    flex-direction: column;
    color: #8c939d;
}

.tree-actions {
    margin-bottom: 15px;
    display: flex;
    align-items: center;
    gap: 10px;
}

.tip {
    font-size: 12px;
    color: #909399;
}

.custom-tree-node {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-right: 8px;
}

.node-label {
    display: flex;
    align-items: center;
    gap: 6px;
}

.node-icon {
    color: #409eff;
}

.video-header {
    display: flex;
    justify-content: flex-end;
}

.search-box {
    margin-bottom: 15px;
}

.uploaded-paper-info {
    margin-top: 15px;
    padding: 10px;
    background: #f0f9eb;
    border-radius: 4px;
}
</style>
